import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useModules } from '@/contexts/ModuleContext';
import ModuleGate from '@/components/ModuleGate';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  GitPullRequest, Plus, Loader2, Trash2, Send, CheckCircle2, XCircle, 
  Clock, AlertTriangle, FileText, MessageSquare, History, Filter,
  ChevronRight, ArrowRight, Pause, Play, Eye, Edit2, MoreHorizontal,
  ExternalLink, Copy, Link2, UserPlus, Mail, ListTodo, CircleCheck, Circle,
  CalendarDays, User, Unlink
} from 'lucide-react';
import { toast } from 'sonner';

const ChangeManagement = ({ user, onLogout }) => {
  const { checkModuleAccess } = useModules();
  const moduleAccess = checkModuleAccess('change_management');

  // State
  const [changeRequests, setChangeRequests] = useState([]);
  const [selectedCR, setSelectedCR] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showFormLinkDialog, setShowFormLinkDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateTasksDialog, setShowCreateTasksDialog] = useState(false);
  const [showLinkTasksDialog, setShowLinkTasksDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [formLinkProject, setFormLinkProject] = useState(null);
  const [formLinkData, setFormLinkData] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [linkedTasks, setLinkedTasks] = useState({ tasks: [], completed: 0, total: 0 });
  const [projectUsers, setProjectUsers] = useState([]);

  // Form state for creating new CR
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    description: '',
    reason: '',
    impact_scope: 'minor',
    impact_schedule: 'none',
    impact_budget: 'none',
    impact_quality: 'none',
    impact_resources: 'none',
    estimated_cost: '',
    estimated_days: '',
    priority: 'medium'
  });

  // Edit state for updating existing CR
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    reason: '',
    impact_scope: 'minor',
    impact_schedule: 'none',
    impact_budget: 'none',
    impact_quality: 'none',
    impact_resources: 'none',
    estimated_cost: '',
    estimated_days: '',
    priority: 'medium'
  });

  // Review state
  const [reviewData, setReviewData] = useState({
    decision: '',
    reviewer_comment: '',
    conditions: ''
  });

  // Comment state
  const [newComment, setNewComment] = useState('');

  // Task creation state
  const [newTasks, setNewTasks] = useState([
    { title: '', description: '', due_date: '', priority: 'medium', assigned_to: '' }
  ]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  useEffect(() => {
    fetchChangeRequests();
    fetchProjects();
  }, []);

  const fetchChangeRequests = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/change-requests`);
      setChangeRequests(response.data);
    } catch (error) {
      console.error('Error fetching change requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const loadCR = async (crId) => {
    try {
      const response = await axios.get(`${API}/change-requests/${crId}`);
      setSelectedCR(response.data);
      // Load linked tasks for this CR
      loadLinkedTasks(crId);
    } catch (error) {
      toast.error('Fehler beim Laden des Change Requests');
    }
  };

  const loadLinkedTasks = async (crId) => {
    try {
      const response = await axios.get(`${API}/change-requests/${crId}/tasks`);
      setLinkedTasks(response.data);
    } catch (error) {
      console.error('Error loading linked tasks:', error);
      setLinkedTasks({ tasks: [], completed: 0, total: 0 });
    }
  };

  const loadProjectTasks = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/tasks`);
      // Filter out tasks that are already linked to this CR
      const unlinkedTasks = response.data.filter(t => 
        !t.change_request_id || t.change_request_id === selectedCR?.id
      );
      setProjectTasks(unlinkedTasks);
    } catch (error) {
      console.error('Error loading project tasks:', error);
      setProjectTasks([]);
    }
  };

  const loadProjectUsers = async (projectId) => {
    try {
      // Get project to find team members
      const projectResponse = await axios.get(`${API}/projects/${projectId}`);
      const project = projectResponse.data;
      
      // Get users
      const usersResponse = await axios.get(`${API}/users`);
      const allUsers = usersResponse.data;
      
      // Filter to project team
      const teamIds = [project.owner_id, ...(project.team_member_ids || [])];
      const teamUsers = allUsers.filter(u => teamIds.includes(u.id));
      setProjectUsers(teamUsers);
    } catch (error) {
      console.error('Error loading project users:', error);
      setProjectUsers([]);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.project_id || !formData.title || !formData.description || !formData.reason) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        estimated_cost: parseFloat(formData.estimated_cost) || 0,
        estimated_days: parseInt(formData.estimated_days) || 0
      };

      const response = await axios.post(`${API}/change-requests`, payload);
      setChangeRequests(prev => [response.data, ...prev]);
      setSelectedCR(response.data);
      setShowCreateDialog(false);
      resetForm();
      toast.success('Change Request erstellt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!selectedCR) return;
    
    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/submit`);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      toast.success('Change Request zur Prüfung eingereicht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen');
    }
  };

  const handleAcceptExternal = async () => {
    if (!selectedCR) return;
    
    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/accept-external`);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      toast.success('Externe Anfrage angenommen - Sie können den CR nun vervollständigen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleRejectExternal = async () => {
    if (!selectedCR) return;
    
    const reason = prompt('Begründung für die Ablehnung (optional):');
    
    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/reject-external?reason=${encodeURIComponent(reason || '')}`);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      toast.success('Externe Anfrage abgelehnt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleEnableFormLink = async (projectId) => {
    try {
      const response = await axios.post(`${API}/change-requests/project/${projectId}/enable-form`);
      setFormLinkData(response.data);
      toast.success('Formular-Link aktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktivieren des Formular-Links');
    }
  };

  const handleDisableFormLink = async (projectId) => {
    try {
      await axios.post(`${API}/change-requests/project/${projectId}/disable-form`);
      setFormLinkData(null);
      toast.success('Formular-Link deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Deaktivieren');
    }
  };

  const openFormLinkDialog = async (project) => {
    setFormLinkProject(project);
    try {
      const response = await axios.get(`${API}/change-requests/project/${project.id}/form-status`);
      setFormLinkData(response.data.enabled ? response.data : null);
    } catch (error) {
      setFormLinkData(null);
    }
    setShowFormLinkDialog(true);
  };

  const copyFormLink = () => {
    if (formLinkData?.form_url) {
      const fullUrl = `${window.location.origin}${formLinkData.form_url}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success('Link in Zwischenablage kopiert');
    }
  };

  const openEditDialog = () => {
    if (!selectedCR) return;
    setEditData({
      title: selectedCR.title || '',
      description: selectedCR.description || '',
      reason: selectedCR.reason || '',
      impact_scope: selectedCR.impact_scope || 'minor',
      impact_schedule: selectedCR.impact_schedule || 'none',
      impact_budget: selectedCR.impact_budget || 'none',
      impact_quality: selectedCR.impact_quality || 'none',
      impact_resources: selectedCR.impact_resources || 'none',
      estimated_cost: selectedCR.estimated_cost || '',
      estimated_days: selectedCR.estimated_days || '',
      priority: selectedCR.priority || 'medium'
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCR) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...editData,
        estimated_cost: parseFloat(editData.estimated_cost) || 0,
        estimated_days: parseInt(editData.estimated_days) || 0
      };

      await axios.put(`${API}/change-requests/${selectedCR.id}`, payload);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      setShowEditDialog(false);
      toast.success('Change Request aktualisiert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReview = async () => {
    if (!selectedCR || !reviewData.decision || !reviewData.reviewer_comment) {
      toast.error('Bitte Entscheidung und Kommentar eingeben');
      return;
    }

    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/review`, reviewData);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      setShowReviewDialog(false);
      setReviewData({ decision: '', reviewer_comment: '', conditions: '' });
      toast.success('Entscheidung gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Prüfung');
    }
  };

  const handleImplement = async () => {
    if (!selectedCR) return;
    
    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/implement`);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      toast.success('Als implementiert markiert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleClose = async () => {
    if (!selectedCR) return;
    
    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/close`);
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      toast.success('Change Request geschlossen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleAddComment = async () => {
    if (!selectedCR || !newComment.trim()) return;
    
    try {
      await axios.post(`${API}/change-requests/${selectedCR.id}/comments`, { text: newComment });
      await loadCR(selectedCR.id);
      setNewComment('');
      toast.success('Kommentar hinzugefügt');
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleDelete = async () => {
    if (!selectedCR) return;
    
    if (!confirm('Diesen Change Request wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/change-requests/${selectedCR.id}`);
      setSelectedCR(null);
      await fetchChangeRequests();
      toast.success('Change Request gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleCreateTasks = async () => {
    if (!selectedCR) return;
    
    // Filter out empty tasks
    const validTasks = newTasks.filter(t => t.title.trim());
    if (validTasks.length === 0) {
      toast.error('Bitte mindestens einen Task mit Titel eingeben');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/change-requests/${selectedCR.id}/create-tasks`, {
        tasks: validTasks
      });
      
      await loadCR(selectedCR.id);
      await fetchChangeRequests();
      setShowCreateTasksDialog(false);
      setNewTasks([{ title: '', description: '', due_date: '', priority: 'medium', assigned_to: '' }]);
      toast.success(`${response.data.created_count} Task(s) erstellt`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Tasks');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLinkTasks = async () => {
    if (!selectedCR || selectedTaskIds.length === 0) {
      toast.error('Bitte mindestens einen Task auswählen');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/change-requests/${selectedCR.id}/link-tasks`, {
        task_ids: selectedTaskIds
      });
      
      await loadCR(selectedCR.id);
      setShowLinkTasksDialog(false);
      setSelectedTaskIds([]);
      toast.success(`${response.data.linked_count} Task(s) verknüpft`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verknüpfen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnlinkTask = async (taskId) => {
    if (!selectedCR) return;
    
    try {
      await axios.delete(`${API}/change-requests/${selectedCR.id}/unlink-task/${taskId}`);
      await loadLinkedTasks(selectedCR.id);
      toast.success('Task-Verknüpfung aufgehoben');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const openCreateTasksDialog = () => {
    if (!selectedCR) return;
    loadProjectUsers(selectedCR.project_id);
    setNewTasks([{ 
      title: `[${selectedCR.cr_number}] `, 
      description: selectedCR.description || '',
      due_date: '', 
      priority: selectedCR.priority || 'medium', 
      assigned_to: '' 
    }]);
    setShowCreateTasksDialog(true);
  };

  const openLinkTasksDialog = () => {
    if (!selectedCR) return;
    loadProjectTasks(selectedCR.project_id);
    setSelectedTaskIds(selectedCR.linked_task_ids || []);
    setShowLinkTasksDialog(true);
  };

  const addNewTaskRow = () => {
    setNewTasks(prev => [...prev, { 
      title: `[${selectedCR?.cr_number}] `, 
      description: '', 
      due_date: '', 
      priority: 'medium', 
      assigned_to: '' 
    }]);
  };

  const removeTaskRow = (index) => {
    if (newTasks.length > 1) {
      setNewTasks(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateTaskRow = (index, field, value) => {
    setNewTasks(prev => prev.map((task, i) => 
      i === index ? { ...task, [field]: value } : task
    ));
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      title: '',
      description: '',
      reason: '',
      impact_scope: 'minor',
      impact_schedule: 'none',
      impact_budget: 'none',
      impact_quality: 'none',
      impact_resources: 'none',
      estimated_cost: '',
      estimated_days: '',
      priority: 'medium'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
      external_submitted: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700',
      submitted: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
      under_review: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
      approved: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
      rejected: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
      deferred: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
      more_info_needed: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
      implemented: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
      closed: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
    };
    const labels = {
      draft: 'Entwurf',
      external_submitted: '📨 Externe Anfrage',
      submitted: 'Eingereicht',
      under_review: 'In Prüfung',
      approved: 'Genehmigt',
      rejected: 'Abgelehnt',
      deferred: 'Zurückgestellt',
      more_info_needed: 'Info benötigt',
      implemented: 'Umgesetzt',
      closed: 'Geschlossen'
    };
    return <Badge className={styles[status] || styles.draft}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
      medium: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
      high: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
      critical: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
    };
    const labels = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch', critical: 'Kritisch' };
    return <Badge className={styles[priority] || styles.medium}>{labels[priority] || priority}</Badge>;
  };

  const getImpactColor = (level) => {
    const colors = { none: 'text-gray-400', low: 'text-green-600 dark:text-green-400', medium: 'text-yellow-600 dark:text-yellow-400', high: 'text-red-600 dark:text-red-400' };
    return colors[level] || colors.none;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(value || 0);
  };

  // Filter change requests
  const filteredCRs = changeRequests.filter(cr => {
    if (filterStatus !== 'all' && cr.status !== filterStatus) return false;
    if (filterProject !== 'all' && cr.project_id !== filterProject) return false;
    return true;
  });

  return (
    <Layout user={user} onLogout={onLogout}>
      <ModuleGate moduleId="change_management">
        <div className="container mx-auto p-6 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <GitPullRequest className="w-6 h-6 text-primary" />
                Change Management
              </h1>
              <p className="text-muted-foreground mt-1">
                Change Requests erfassen, bewerten und genehmigen
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                if (projects.length > 0) {
                  openFormLinkDialog(projects[0]);
                } else {
                  toast.error('Bitte zuerst ein Projekt auswählen');
                }
              }}>
                <Link2 className="w-4 h-4 mr-2" />
                Externes Formular
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="create-cr-btn">
                <Plus className="w-4 h-4 mr-2" />
                Neuer Change Request
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Status:</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      <SelectItem value="external_submitted">📨 Externe Anfragen</SelectItem>
                      <SelectItem value="draft">Entwurf</SelectItem>
                      <SelectItem value="submitted">Eingereicht</SelectItem>
                      <SelectItem value="approved">Genehmigt</SelectItem>
                      <SelectItem value="rejected">Abgelehnt</SelectItem>
                      <SelectItem value="implemented">Umgesetzt</SelectItem>
                      <SelectItem value="closed">Geschlossen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Projekt:</Label>
                  <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Projekte</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground ml-auto">
                  {filteredCRs.length} Change Requests
                  {changeRequests.filter(cr => cr.status === 'external_submitted').length > 0 && (
                    <Badge className="ml-2 bg-amber-100 text-amber-700">
                      {changeRequests.filter(cr => cr.status === 'external_submitted').length} neue externe
                    </Badge>
                  )}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* CR List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Change Requests</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[600px]">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : filteredCRs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <GitPullRequest className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Keine Change Requests</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredCRs.map(cr => (
                          <div
                            key={cr.id}
                            onClick={() => loadCR(cr.id)}
                            className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${selectedCR?.id === cr.id ? 'bg-muted' : ''}`}
                            data-testid={`cr-item-${cr.cr_number}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <span className="font-mono text-sm text-muted-foreground">{cr.cr_number}</span>
                              {getStatusBadge(cr.status)}
                            </div>
                            <h4 className="font-medium text-sm mb-1 line-clamp-2">{cr.title}</h4>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{cr.project_name}</span>
                              <span>•</span>
                              {getPriorityBadge(cr.priority)}
                            </div>
                            {cr.estimated_cost > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {formatCurrency(cr.estimated_cost)} • {cr.estimated_days} Tage
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* CR Detail */}
            <div className="lg:col-span-2">
              {selectedCR ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-sm text-muted-foreground">{selectedCR.cr_number}</span>
                          {getStatusBadge(selectedCR.status)}
                          {getPriorityBadge(selectedCR.priority)}
                        </div>
                        <CardTitle>{selectedCR.title}</CardTitle>
                        <CardDescription>
                          Projekt: {selectedCR.project_name} • Erstellt von {selectedCR.created_by_name} am {formatDate(selectedCR.created_at)}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        {selectedCR.status === 'draft' && (
                          <>
                            <Button size="sm" variant="outline" onClick={handleDelete}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={openEditDialog}>
                              <Edit2 className="w-4 h-4 mr-1" />
                              Bearbeiten
                            </Button>
                            <Button size="sm" onClick={handleSubmitForReview} data-testid="submit-for-review-btn">
                              <Send className="w-4 h-4 mr-1" />
                              Einreichen
                            </Button>
                          </>
                        )}
                        {selectedCR.status === 'more_info_needed' && (
                          <>
                            <Button size="sm" variant="outline" onClick={openEditDialog}>
                              <Edit2 className="w-4 h-4 mr-1" />
                              Bearbeiten
                            </Button>
                            <Button size="sm" onClick={handleSubmitForReview} data-testid="submit-for-review-btn">
                              <Send className="w-4 h-4 mr-1" />
                              Erneut einreichen
                            </Button>
                          </>
                        )}
                        {selectedCR.status === 'deferred' && (
                          <>
                            <Button size="sm" variant="outline" onClick={openEditDialog}>
                              <Edit2 className="w-4 h-4 mr-1" />
                              Bearbeiten
                            </Button>
                            <Button size="sm" onClick={handleSubmitForReview}>
                              <Send className="w-4 h-4 mr-1" />
                              Erneut einreichen
                            </Button>
                          </>
                        )}
                        {selectedCR.status === 'external_submitted' && (
                          <>
                            <Button size="sm" variant="outline" onClick={handleRejectExternal} className="text-red-600 hover:text-red-700">
                              <XCircle className="w-4 h-4 mr-1" />
                              Ablehnen
                            </Button>
                            <Button size="sm" onClick={handleAcceptExternal} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Annehmen & Bearbeiten
                            </Button>
                          </>
                        )}
                        {selectedCR.status === 'submitted' && (
                          <Button size="sm" onClick={() => setShowReviewDialog(true)} data-testid="review-btn">
                            <Eye className="w-4 h-4 mr-1" />
                            Prüfen
                          </Button>
                        )}
                        {selectedCR.status === 'approved' && (
                          <>
                            <Button size="sm" variant="outline" onClick={openLinkTasksDialog} data-testid="link-tasks-btn">
                              <Link2 className="w-4 h-4 mr-1" />
                              Tasks verknüpfen
                            </Button>
                            <Button size="sm" variant="outline" onClick={openCreateTasksDialog} data-testid="create-tasks-btn">
                              <ListTodo className="w-4 h-4 mr-1" />
                              Tasks erstellen
                            </Button>
                            <Button size="sm" onClick={handleImplement} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Als umgesetzt markieren
                            </Button>
                          </>
                        )}
                        {(selectedCR.status === 'implemented' || selectedCR.status === 'rejected') && (
                          <Button size="sm" variant="outline" onClick={handleClose}>
                            Schliessen
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="details">
                      <TabsList className="mb-4">
                        <TabsTrigger value="details">Details</TabsTrigger>
                        {selectedCR.is_external && <TabsTrigger value="requester">Antragsteller</TabsTrigger>}
                        <TabsTrigger value="impact">Impact-Analyse</TabsTrigger>
                        {(selectedCR.status === 'approved' || selectedCR.status === 'implemented' || linkedTasks.total > 0) && (
                          <TabsTrigger value="tasks" className="flex items-center gap-1">
                            Tasks
                            {linkedTasks.total > 0 && (
                              <Badge variant="secondary" className="ml-1 text-xs">
                                {linkedTasks.completed}/{linkedTasks.total}
                              </Badge>
                            )}
                          </TabsTrigger>
                        )}
                        <TabsTrigger value="review">Prüfung</TabsTrigger>
                        <TabsTrigger value="comments">Kommentare ({selectedCR.comments?.length || 0})</TabsTrigger>
                        <TabsTrigger value="history">Verlauf</TabsTrigger>
                      </TabsList>

                      {/* Details Tab */}
                      <TabsContent value="details" className="space-y-4">
                        {selectedCR.is_external && (
                          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-4">
                            <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-1">
                              <Mail className="w-4 h-4" />
                              Externe Anfrage
                            </div>
                            <p className="text-xs text-amber-600">
                              Von {selectedCR.requester_name} ({selectedCR.requester_email})
                              {selectedCR.requester_department && ` • ${selectedCR.requester_department}`}
                            </p>
                          </div>
                        )}
                        <div>
                          <Label className="text-muted-foreground text-xs">Beschreibung</Label>
                          <p className="text-sm whitespace-pre-wrap mt-1">{selectedCR.description}</p>
                        </div>
                        <Separator />
                        <div>
                          <Label className="text-muted-foreground text-xs">Begründung</Label>
                          <p className="text-sm whitespace-pre-wrap mt-1">{selectedCR.reason}</p>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-muted-foreground text-xs">Geschätzte Kosten</Label>
                            <p className="text-lg font-semibold">{formatCurrency(selectedCR.estimated_cost)}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground text-xs">Geschätzte Dauer</Label>
                            <p className="text-lg font-semibold">{selectedCR.estimated_days} Tage</p>
                          </div>
                        </div>
                      </TabsContent>

                      {/* Requester Tab (for external CRs) */}
                      {selectedCR.is_external && (
                        <TabsContent value="requester" className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg border">
                              <Label className="text-xs text-muted-foreground">Name</Label>
                              <p className="font-medium">{selectedCR.requester_name}</p>
                            </div>
                            <div className="p-3 rounded-lg border">
                              <Label className="text-xs text-muted-foreground">E-Mail</Label>
                              <p className="font-medium">
                                <a href={`mailto:${selectedCR.requester_email}`} className="text-blue-600 hover:underline">
                                  {selectedCR.requester_email}
                                </a>
                              </p>
                            </div>
                            {selectedCR.requester_department && (
                              <div className="p-3 rounded-lg border col-span-2">
                                <Label className="text-xs text-muted-foreground">Abteilung</Label>
                                <p className="font-medium">{selectedCR.requester_department}</p>
                              </div>
                            )}
                          </div>
                        </TabsContent>
                      )}
                      <TabsContent value="impact" className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                          <span className="font-medium">Impact Score</span>
                          <div className="flex items-center gap-2">
                            <div className={`text-2xl font-bold ${selectedCR.impact_score >= 15 ? 'text-red-600' : selectedCR.impact_score >= 10 ? 'text-orange-600' : selectedCR.impact_score >= 5 ? 'text-yellow-600' : 'text-green-600'}`}>
                              {selectedCR.impact_score}
                            </div>
                            <span className="text-muted-foreground">/ 20</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 rounded-lg border">
                            <Label className="text-xs text-muted-foreground">Umfang</Label>
                            <p className={`font-medium capitalize ${selectedCR.impact_scope === 'critical' ? 'text-red-600' : selectedCR.impact_scope === 'major' ? 'text-orange-600' : ''}`}>
                              {selectedCR.impact_scope === 'minor' ? 'Gering' : selectedCR.impact_scope === 'moderate' ? 'Moderat' : selectedCR.impact_scope === 'major' ? 'Gross' : 'Kritisch'}
                            </p>
                          </div>
                          <div className="p-3 rounded-lg border">
                            <Label className="text-xs text-muted-foreground">Zeitplan</Label>
                            <p className={`font-medium capitalize ${getImpactColor(selectedCR.impact_schedule)}`}>
                              {selectedCR.impact_schedule === 'none' ? 'Kein' : selectedCR.impact_schedule === 'low' ? 'Gering' : selectedCR.impact_schedule === 'medium' ? 'Mittel' : 'Hoch'}
                            </p>
                          </div>
                          <div className="p-3 rounded-lg border">
                            <Label className="text-xs text-muted-foreground">Budget</Label>
                            <p className={`font-medium capitalize ${getImpactColor(selectedCR.impact_budget)}`}>
                              {selectedCR.impact_budget === 'none' ? 'Kein' : selectedCR.impact_budget === 'low' ? 'Gering' : selectedCR.impact_budget === 'medium' ? 'Mittel' : 'Hoch'}
                            </p>
                          </div>
                          <div className="p-3 rounded-lg border">
                            <Label className="text-xs text-muted-foreground">Qualität</Label>
                            <p className={`font-medium capitalize ${getImpactColor(selectedCR.impact_quality)}`}>
                              {selectedCR.impact_quality === 'none' ? 'Kein' : selectedCR.impact_quality === 'low' ? 'Gering' : selectedCR.impact_quality === 'medium' ? 'Mittel' : 'Hoch'}
                            </p>
                          </div>
                          <div className="p-3 rounded-lg border">
                            <Label className="text-xs text-muted-foreground">Ressourcen</Label>
                            <p className={`font-medium capitalize ${getImpactColor(selectedCR.impact_resources)}`}>
                              {selectedCR.impact_resources === 'none' ? 'Kein' : selectedCR.impact_resources === 'low' ? 'Gering' : selectedCR.impact_resources === 'medium' ? 'Mittel' : 'Hoch'}
                            </p>
                          </div>
                        </div>
                      </TabsContent>

                      {/* Tasks Tab */}
                      {(selectedCR.status === 'approved' || selectedCR.status === 'implemented' || linkedTasks.total > 0) && (
                        <TabsContent value="tasks" className="space-y-4">
                          {/* Progress Header */}
                          {linkedTasks.total > 0 && (
                            <div className="p-4 rounded-lg bg-muted/50">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">Fortschritt</span>
                                <span className="text-sm text-muted-foreground">
                                  {linkedTasks.completed} von {linkedTasks.total} Tasks abgeschlossen
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                <div 
                                  className={`h-2.5 rounded-full transition-all duration-300 ${
                                    linkedTasks.all_completed ? 'bg-green-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${linkedTasks.total > 0 ? (linkedTasks.completed / linkedTasks.total) * 100 : 0}%` }}
                                />
                              </div>
                              {linkedTasks.all_completed && (
                                <div className="flex items-center gap-2 mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-green-700">
                                    Alle Tasks abgeschlossen! Der CR wird automatisch als "Umgesetzt" markiert.
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          {selectedCR.status === 'approved' && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={openCreateTasksDialog}>
                                <Plus className="w-4 h-4 mr-1" />
                                Neue Tasks erstellen
                              </Button>
                              <Button size="sm" variant="outline" onClick={openLinkTasksDialog}>
                                <Link2 className="w-4 h-4 mr-1" />
                                Bestehende Tasks verknüpfen
                              </Button>
                            </div>
                          )}

                          {/* Task List */}
                          <ScrollArea className="h-64">
                            {linkedTasks.tasks?.length > 0 ? (
                              <div className="space-y-2">
                                {linkedTasks.tasks.map(task => (
                                  <div 
                                    key={task.id} 
                                    className={`flex items-center justify-between p-3 rounded-lg border ${
                                      task.completed || task.status === 'completed' 
                                        ? 'bg-green-50 border-green-200' 
                                        : 'bg-white dark:bg-slate-800'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {task.completed || task.status === 'completed' ? (
                                        <CircleCheck className="w-5 h-5 text-green-600" />
                                      ) : (
                                        <Circle className="w-5 h-5 text-gray-400" />
                                      )}
                                      <div>
                                        <p className={`font-medium text-sm ${
                                          task.completed || task.status === 'completed' 
                                            ? 'text-green-700 line-through' 
                                            : ''
                                        }`}>
                                          {task.title}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                          {task.assigned_to_name && (
                                            <span className="flex items-center gap-1">
                                              <User className="w-3 h-3" />
                                              {task.assigned_to_name}
                                            </span>
                                          )}
                                          {task.due_date && (
                                            <span className="flex items-center gap-1">
                                              <CalendarDays className="w-3 h-3" />
                                              {new Date(task.due_date).toLocaleDateString('de-CH')}
                                            </span>
                                          )}
                                          {task.priority && getPriorityBadge(task.priority)}
                                        </div>
                                      </div>
                                    </div>
                                    {selectedCR.status === 'approved' && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => handleUnlinkTask(task.id)}
                                        title="Verknüpfung aufheben"
                                      >
                                        <Unlink className="w-4 h-4 text-muted-foreground" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-muted-foreground">
                                <ListTodo className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                <p>Keine Tasks verknüpft</p>
                                {selectedCR.status === 'approved' && (
                                  <p className="text-xs mt-1">
                                    Erstellen Sie Tasks oder verknüpfen Sie bestehende Tasks mit diesem CR.
                                  </p>
                                )}
                              </div>
                            )}
                          </ScrollArea>
                        </TabsContent>
                      )}

                      {/* Review Tab */}
                      <TabsContent value="review" className="space-y-4">
                        {selectedCR.decision ? (
                          <div className={`p-4 rounded-lg ${selectedCR.decision === 'approved' ? 'bg-green-50 border-green-200' : selectedCR.decision === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} border`}>
                            <div className="flex items-center gap-2 mb-2">
                              {selectedCR.decision === 'approved' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : selectedCR.decision === 'rejected' ? <XCircle className="w-5 h-5 text-red-600" /> : <Clock className="w-5 h-5 text-yellow-600" />}
                              <span className="font-semibold">
                                {selectedCR.decision === 'approved' ? 'Genehmigt' : selectedCR.decision === 'rejected' ? 'Abgelehnt' : selectedCR.decision === 'deferred' ? 'Zurückgestellt' : 'Weitere Info benötigt'}
                              </span>
                            </div>
                            <p className="text-sm mb-2">{selectedCR.reviewer_comment}</p>
                            {selectedCR.conditions && (
                              <div className="mt-2 p-2 bg-white/50 dark:bg-slate-800/50 rounded">
                                <Label className="text-xs text-muted-foreground">Bedingungen</Label>
                                <p className="text-sm">{selectedCR.conditions}</p>
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Geprüft von {selectedCR.reviewed_by_name} am {formatDate(selectedCR.reviewed_at)}
                            </p>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Noch nicht geprüft</p>
                          </div>
                        )}
                      </TabsContent>

                      {/* Comments Tab */}
                      <TabsContent value="comments" className="space-y-4">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Kommentar hinzufügen..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                          />
                          <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                        <ScrollArea className="h-64">
                          {selectedCR.comments?.length > 0 ? (
                            <div className="space-y-3">
                              {selectedCR.comments.map(comment => (
                                <div key={comment.id} className="p-3 rounded-lg bg-muted/50">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-sm">{comment.user_name}</span>
                                    <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                                  </div>
                                  <p className="text-sm">{comment.text}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-muted-foreground py-4">Keine Kommentare</p>
                          )}
                        </ScrollArea>
                      </TabsContent>

                      {/* History Tab */}
                      <TabsContent value="history">
                        <ScrollArea className="h-64">
                          {selectedCR.history?.length > 0 ? (
                            <div className="space-y-2">
                              {selectedCR.history.slice().reverse().map(entry => (
                                <div key={entry.id} className="flex items-start gap-3 p-2">
                                  <History className="w-4 h-4 text-muted-foreground mt-0.5" />
                                  <div>
                                    <p className="text-sm">{entry.details}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {entry.user_name} • {formatDate(entry.timestamp)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-muted-foreground py-4">Kein Verlauf</p>
                          )}
                        </ScrollArea>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-[600px] flex items-center justify-center">
                  <div className="text-center p-8">
                    <GitPullRequest className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="text-lg font-medium text-muted-foreground mb-2">
                      Kein Change Request ausgewählt
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Wählen Sie einen Change Request aus der Liste oder erstellen Sie einen neuen.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Neuer Change Request</DialogTitle>
              <DialogDescription>
                Erfassen Sie einen neuen Änderungsantrag
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Projekt *</Label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData(prev => ({ ...prev, project_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Projekt wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Titel *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Kurze Beschreibung der Änderung"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Beschreibung *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detaillierte Beschreibung der gewünschten Änderung"
                    rows={3}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Begründung *</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Warum ist diese Änderung notwendig?"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Priorität</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Niedrig</SelectItem>
                      <SelectItem value="medium">Mittel</SelectItem>
                      <SelectItem value="high">Hoch</SelectItem>
                      <SelectItem value="critical">Kritisch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Umfang</Label>
                  <Select value={formData.impact_scope} onValueChange={(v) => setFormData(prev => ({ ...prev, impact_scope: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Gering</SelectItem>
                      <SelectItem value="moderate">Moderat</SelectItem>
                      <SelectItem value="major">Gross</SelectItem>
                      <SelectItem value="critical">Kritisch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Impact Zeitplan</Label>
                  <Select value={formData.impact_schedule} onValueChange={(v) => setFormData(prev => ({ ...prev, impact_schedule: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein</SelectItem>
                      <SelectItem value="low">Gering</SelectItem>
                      <SelectItem value="medium">Mittel</SelectItem>
                      <SelectItem value="high">Hoch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Impact Budget</Label>
                  <Select value={formData.impact_budget} onValueChange={(v) => setFormData(prev => ({ ...prev, impact_budget: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein</SelectItem>
                      <SelectItem value="low">Gering</SelectItem>
                      <SelectItem value="medium">Mittel</SelectItem>
                      <SelectItem value="high">Hoch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Geschätzte Kosten (CHF)</Label>
                  <Input
                    type="number"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimated_cost: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Geschätzte Dauer (Tage)</Label>
                  <Input
                    type="number"
                    value={formData.estimated_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimated_days: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Request prüfen</DialogTitle>
              <DialogDescription>
                Treffen Sie eine Entscheidung zu diesem Änderungsantrag
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Entscheidung *</Label>
                <Select value={reviewData.decision} onValueChange={(v) => setReviewData(prev => ({ ...prev, decision: v }))}>
                  <SelectTrigger><SelectValue placeholder="Entscheidung wählen..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Genehmigen</SelectItem>
                    <SelectItem value="rejected">Ablehnen</SelectItem>
                    <SelectItem value="deferred">Zurückstellen</SelectItem>
                    <SelectItem value="more_info_needed">Weitere Info benötigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kommentar *</Label>
                <Textarea
                  value={reviewData.reviewer_comment}
                  onChange={(e) => setReviewData(prev => ({ ...prev, reviewer_comment: e.target.value }))}
                  placeholder="Begründung für die Entscheidung"
                  rows={3}
                />
              </div>
              {reviewData.decision === 'approved' && (
                <div>
                  <Label>Bedingungen (optional)</Label>
                  <Textarea
                    value={reviewData.conditions}
                    onChange={(e) => setReviewData(prev => ({ ...prev, conditions: e.target.value }))}
                    placeholder="Bedingungen für die Genehmigung"
                    rows={2}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Abbrechen</Button>
              <Button onClick={handleReview} disabled={!reviewData.decision || !reviewData.reviewer_comment}>
                Entscheidung speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* External Form Link Dialog */}
        <Dialog open={showFormLinkDialog} onOpenChange={setShowFormLinkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Externes Formular verwalten
              </DialogTitle>
              <DialogDescription>
                Erstellen Sie einen öffentlichen Link, über den externe Benutzer Change Requests einreichen können.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Projekt auswählen</Label>
                <Select 
                  value={formLinkProject?.id || ''} 
                  onValueChange={(v) => {
                    const project = projects.find(p => p.id === v);
                    if (project) openFormLinkDialog(project);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Projekt wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formLinkProject && (
                <>
                  {formLinkData ? (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                        <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Formular aktiv
                        </div>
                        <div className="flex items-center gap-2">
                          <Input 
                            value={`${window.location.origin}${formLinkData.form_url}`}
                            readOnly 
                            className="text-sm"
                          />
                          <Button size="sm" variant="outline" onClick={copyFormLink}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-green-600 mt-2">
                          Teilen Sie diesen Link mit externen Benutzern, die Change Requests einreichen sollen.
                        </p>
                      </div>
                      
                      <div className="p-3 rounded-lg bg-muted/50 text-sm">
                        <p className="font-medium mb-2">Was passiert mit externen Anfragen?</p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li>Externe Anfragen erscheinen mit Status "📨 Externe Anfrage"</li>
                          <li>Der Projektleiter kann die Anfrage annehmen oder ablehnen</li>
                          <li>Nach Annahme wird der CR vervollständigt und normal bearbeitet</li>
                        </ul>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full text-red-600 hover:text-red-700"
                        onClick={() => handleDisableFormLink(formLinkProject.id)}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Formular deaktivieren
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-muted/50 text-center">
                        <ExternalLink className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Das externe Formular ist für dieses Projekt noch nicht aktiviert.
                        </p>
                      </div>
                      
                      <Button 
                        className="w-full"
                        onClick={() => handleEnableFormLink(formLinkProject.id)}
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Externes Formular aktivieren
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFormLinkDialog(false)}>
                Schliessen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit CR Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5" />
                Change Request bearbeiten
              </DialogTitle>
              <DialogDescription>
                Vervollständigen Sie die Impact-Analyse und Kostenschätzung
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Basic Info */}
              <div className="space-y-4 p-4 rounded-lg bg-muted/30">
                <h4 className="font-medium text-sm">Grunddaten</h4>
                <div>
                  <Label>Titel</Label>
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Beschreibung</Label>
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Begründung</Label>
                  <Textarea
                    value={editData.reason}
                    onChange={(e) => setEditData(prev => ({ ...prev, reason: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Priorität</Label>
                  <Select value={editData.priority} onValueChange={(v) => setEditData(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Niedrig</SelectItem>
                      <SelectItem value="medium">Mittel</SelectItem>
                      <SelectItem value="high">Hoch</SelectItem>
                      <SelectItem value="critical">Kritisch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Impact Analysis */}
              <div className="space-y-4 p-4 rounded-lg bg-blue-50 border border-blue-100">
                <h4 className="font-medium text-sm text-blue-800">Impact-Analyse (vom Projektleiter auszufüllen)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Umfang der Änderung</Label>
                    <Select value={editData.impact_scope} onValueChange={(v) => setEditData(prev => ({ ...prev, impact_scope: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minor">Gering - Kleine Anpassung</SelectItem>
                        <SelectItem value="moderate">Moderat - Mehrere Komponenten</SelectItem>
                        <SelectItem value="major">Gross - Signifikante Änderung</SelectItem>
                        <SelectItem value="critical">Kritisch - Systemweite Auswirkung</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Impact auf Zeitplan</Label>
                    <Select value={editData.impact_schedule} onValueChange={(v) => setEditData(prev => ({ ...prev, impact_schedule: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Einfluss</SelectItem>
                        <SelectItem value="low">Gering - Kleine Verzögerung möglich</SelectItem>
                        <SelectItem value="medium">Mittel - Meilenstein gefährdet</SelectItem>
                        <SelectItem value="high">Hoch - Projektende gefährdet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Impact auf Budget</Label>
                    <Select value={editData.impact_budget} onValueChange={(v) => setEditData(prev => ({ ...prev, impact_budget: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Einfluss</SelectItem>
                        <SelectItem value="low">Gering - {"<"}5% Budgeterhöhung</SelectItem>
                        <SelectItem value="medium">Mittel - 5-15% Budgeterhöhung</SelectItem>
                        <SelectItem value="high">Hoch - {">"}15% Budgeterhöhung</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Impact auf Qualität</Label>
                    <Select value={editData.impact_quality} onValueChange={(v) => setEditData(prev => ({ ...prev, impact_quality: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Einfluss</SelectItem>
                        <SelectItem value="low">Gering - Minimale Auswirkung</SelectItem>
                        <SelectItem value="medium">Mittel - Qualitätsanpassung nötig</SelectItem>
                        <SelectItem value="high">Hoch - Qualitätsziele gefährdet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Impact auf Ressourcen</Label>
                    <Select value={editData.impact_resources} onValueChange={(v) => setEditData(prev => ({ ...prev, impact_resources: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Einfluss</SelectItem>
                        <SelectItem value="low">Gering - Bestehendes Team reicht</SelectItem>
                        <SelectItem value="medium">Mittel - Zusätzliche Kapazität nötig</SelectItem>
                        <SelectItem value="high">Hoch - Externe Ressourcen nötig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Cost Estimation */}
              <div className="space-y-4 p-4 rounded-lg bg-amber-50 border border-amber-100">
                <h4 className="font-medium text-sm text-amber-800">Kostenschätzung (vom Projektleiter auszufüllen)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Geschätzte Kosten (CHF)</Label>
                    <Input
                      type="number"
                      value={editData.estimated_cost}
                      onChange={(e) => setEditData(prev => ({ ...prev, estimated_cost: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Geschätzte Dauer (Tage)</Label>
                    <Input
                      type="number"
                      value={editData.estimated_days}
                      onChange={(e) => setEditData(prev => ({ ...prev, estimated_days: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Tasks Dialog */}
        <Dialog open={showCreateTasksDialog} onOpenChange={setShowCreateTasksDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListTodo className="w-5 h-5" />
                Tasks aus Change Request erstellen
              </DialogTitle>
              <DialogDescription>
                Erstellen Sie neue Tasks, die mit diesem CR verknüpft werden. 
                Sobald alle Tasks abgeschlossen sind, wird der CR automatisch als "Umgesetzt" markiert.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* CR Info */}
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-muted-foreground">{selectedCR?.cr_number}</span>
                  <span className="font-medium">{selectedCR?.title}</span>
                </div>
                <p className="text-muted-foreground text-xs line-clamp-2">{selectedCR?.description}</p>
              </div>

              {/* Task Rows */}
              <div className="space-y-3">
                {newTasks.map((task, index) => (
                  <div key={index} className="p-4 rounded-lg border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Task {index + 1}</span>
                      {newTasks.length > 1 && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeTaskRow(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label className="text-xs">Titel *</Label>
                        <Input
                          value={task.title}
                          onChange={(e) => updateTaskRow(index, 'title', e.target.value)}
                          placeholder="Task-Titel"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Beschreibung</Label>
                        <Textarea
                          value={task.description}
                          onChange={(e) => updateTaskRow(index, 'description', e.target.value)}
                          placeholder="Optionale Beschreibung"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Fälligkeitsdatum</Label>
                        <Input
                          type="date"
                          value={task.due_date}
                          onChange={(e) => updateTaskRow(index, 'due_date', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Priorität</Label>
                        <Select value={task.priority} onValueChange={(v) => updateTaskRow(index, 'priority', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Niedrig</SelectItem>
                            <SelectItem value="medium">Mittel</SelectItem>
                            <SelectItem value="high">Hoch</SelectItem>
                            <SelectItem value="critical">Kritisch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Zugewiesen an</Label>
                        <Select value={task.assigned_to} onValueChange={(v) => updateTaskRow(index, 'assigned_to', v)}>
                          <SelectTrigger><SelectValue placeholder="Person auswählen..." /></SelectTrigger>
                          <SelectContent>
                            {projectUsers.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Row Button */}
              <Button variant="outline" className="w-full" onClick={addNewTaskRow}>
                <Plus className="w-4 h-4 mr-2" />
                Weiteren Task hinzufügen
              </Button>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateTasksDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleCreateTasks} disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {newTasks.filter(t => t.title.trim()).length} Task(s) erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Tasks Dialog */}
        <Dialog open={showLinkTasksDialog} onOpenChange={setShowLinkTasksDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Bestehende Tasks verknüpfen
              </DialogTitle>
              <DialogDescription>
                Wählen Sie Tasks aus dem Projekt "{selectedCR?.project_name}", die mit diesem CR verknüpft werden sollen.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* CR Info */}
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-muted-foreground">{selectedCR?.cr_number}</span>
                  <span className="font-medium">{selectedCR?.title}</span>
                </div>
              </div>

              {/* Task Selection */}
              <ScrollArea className="h-[300px]">
                {projectTasks.length > 0 ? (
                  <div className="space-y-2">
                    {projectTasks.map(task => {
                      const isLinked = selectedTaskIds.includes(task.id);
                      const isAlreadyLinked = task.change_request_id && task.change_request_id !== selectedCR?.id;
                      
                      return (
                        <div 
                          key={task.id}
                          onClick={() => {
                            if (isAlreadyLinked) return;
                            if (isLinked) {
                              setSelectedTaskIds(prev => prev.filter(id => id !== task.id));
                            } else {
                              setSelectedTaskIds(prev => [...prev, task.id]);
                            }
                          }}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            isAlreadyLinked 
                              ? 'bg-gray-50 dark:bg-gray-800 opacity-50 cursor-not-allowed' 
                              : isLinked 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                            isLinked ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isLinked && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                {task.title}
                              </p>
                              {task.completed && (
                                <Badge variant="secondary" className="text-xs">Erledigt</Badge>
                              )}
                              {isAlreadyLinked && (
                                <Badge variant="outline" className="text-xs">Bereits verknüpft</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              {task.assigned_to_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_to_name}
                                </span>
                              )}
                              {task.due_date && (
                                <span className="flex items-center gap-1">
                                  <CalendarDays className="w-3 h-3" />
                                  {new Date(task.due_date).toLocaleDateString('de-CH')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ListTodo className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Keine Tasks im Projekt verfügbar</p>
                  </div>
                )}
              </ScrollArea>

              {/* Selection Summary */}
              {selectedTaskIds.length > 0 && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                  <span className="text-blue-700">
                    {selectedTaskIds.length} Task(s) ausgewählt
                  </span>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkTasksDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleLinkTasks} disabled={isSubmitting || selectedTaskIds.length === 0}>
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Verknüpfen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ModuleGate>
    </Layout>
  );
};

export default ChangeManagement;
